import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { OVIDProvider } from '@outdoor-voices/ui/dist/utils/customer/OVIDProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import AccountNav from '@outdoor-voices/ui/dist/components/AccountNav';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { COOKIE_DOMAIN, JWT_COOKIE_NAME } from './constants';
import { lineItemsToItems } from '../../components/Tracking/itemsToProducts';
import { OV_ID_URL } from '../../config/theme';
import objToSearchParams from '../../utils/objects/toSearchParams';
import OVApp from '../../utils/OVContext';
import Tracking from '../../components/Tracking';
import { jsx as _jsx } from "react/jsx-runtime";
var LOADING = 'LOADING';
var MAX_RETRIES = 10;
var _window$OV = window.OV,
    CUSTOMER = _window$OV.CUSTOMER,
    NS_ENDPOINT = _window$OV.NS_ENDPOINT,
    ORDER = _window$OV.ORDER;

var onFail = function onFail(exception) {
  return Sentry.captureException(exception, {
    extra: {
      context: 'order'
    }
  });
};

var showReturnMessage = function showReturnMessage() {
  var returnMessage = document.querySelector('#return-message');

  if (!returnMessage) {
    return;
  }

  returnMessage.style.display = '';
};

var getAddressLinesFromObj = function getAddressLinesFromObj(address) {
  var addressLines = [];
  if (address && address.name) addressLines.push(address.name);
  if (address && address.address1) addressLines.push(address.address1);

  if (address && address.city && address.province_code && address.zip) {
    addressLines.push("".concat(address.city, " ").concat(address.province_code, " ").concat(address.zip));
  }

  if (address && address.country) addressLines.push(address.country);
  return addressLines;
}; // Used for when ns endpoint does not return.
// Formats shopify order data from order.liquid to match ns response the component expects as props

/* eslint-disable camelcase */


export var normalizeShopifyData = function normalizeShopifyData(_ref) {
  var bopis = _ref.bopis,
      billingAddress = _ref.billingAddress,
      lineItems = _ref.lineItems,
      order_name = _ref.number,
      order_date = _ref.order_date,
      shippingAddress = _ref.shippingAddress,
      status = _ref.status;
  // addresses
  var billingAddressLines = getAddressLinesFromObj(billingAddress);
  var shippingAddressLines = bopis ? [] : getAddressLinesFromObj(shippingAddress); // build tracker object (Tracking/Tracker.js propTypes) from shopify data

  var tracker = {
    tracker: {
      carrier: '',
      tracking_code: '',
      status: status.toLowerCase(),
      tracking_details: [],
      est_delivery_date: '',
      public_url: '',
      id: 'shopify'
    },
    tracking_url: '',
    items: lineItemsToItems(lineItems),
    order_name: order_name,
    order_date: order_date,
    shipping_address: shippingAddressLines.join('\n'),
    billing_address: billingAddressLines.join('\n')
  };
  return tracker;
  /* eslint-enable camelcase */
};

var Loader = function Loader(_ref2) {
  var bopis = _ref2.bopis,
      cancelled = _ref2.cancelled,
      cancelledAt = _ref2.cancelledAt,
      fulfillmentStatus = _ref2.fulfillmentStatus,
      shopifyTracker = _ref2.shopifyTracker;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      loaded = _useState2[0],
      setLoaded = _useState2[1];

  var _useState3 = useState([shopifyTracker]),
      _useState4 = _slicedToArray(_useState3, 2),
      trackers = _useState4[0],
      setTrackers = _useState4[1];

  var fetchCount = useRef(1);
  /* eslint-disable prettier/prettier */

  var transactions = ORDER.transactions ? ORDER.transactions.filter(function (_ref3) {
    var kind = _ref3.kind,
        status = _ref3.status;
    return status === 'success' && (kind === 'authorization' || kind === 'sale');
  }) : false;
  var checkForData = useCallback(function (response, fetchFunc) {
    var pendingResponse = !response || !response.status || response.status === LOADING;
    var withinRetryLimit = fetchCount.current < MAX_RETRIES;
    var shouldFetch = pendingResponse && withinRetryLimit;

    if (loaded) {
      return;
    }

    if (shouldFetch) {
      fetchCount.current += 1;
      setTimeout(function () {
        return fetchFunc();
      }, 1000);
      return;
    }

    if (!withinRetryLimit) {
      setLoaded(true);
      return;
    }

    var shopifyIdsBySKU = {};
    var variantIdsBySKU = {};
    trackers.forEach(function (_ref4) {
      var items = _ref4.items;
      items.forEach(function (_ref5) {
        var sku = _ref5.sku,
            shopifyId = _ref5.shopifyId,
            variantId = _ref5.variantId;
        shopifyIdsBySKU[sku] = shopifyId;
        variantIdsBySKU[sku] = variantId;
      });
    });

    if (response.trackers.length > 0) {
      response.trackers.forEach(function (_ref6) {
        var items = _ref6.items;
        items.forEach(function (item) {
          item.shopifyId = shopifyIdsBySKU[item.sku];
          item.variantId = variantIdsBySKU[item.sku];
        });
      });
      setTrackers(response.trackers);
    }

    setLoaded(true);
  }, [loaded, trackers]);
  var fetchOrder = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var email, id, token, params;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            email = CUSTOMER.email, id = CUSTOMER.id, token = CUSTOMER.token;
            params = {
              email: email,
              id: id,
              token: token
            };
            return _context2.abrupt("return", fetch("".concat(NS_ENDPOINT, "orders/").concat(ORDER.number, "/?").concat(objToSearchParams(params))).then( /*#__PURE__*/function () {
              var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(resp) {
                var data;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        if (!(resp.status === 404)) {
                          _context.next = 3;
                          break;
                        }

                        setLoaded(true);
                        return _context.abrupt("return", false);

                      case 3:
                        _context.next = 5;
                        return resp.json();

                      case 5:
                        data = _context.sent;
                        return _context.abrupt("return", data);

                      case 7:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              }));

              return function (_x) {
                return _ref8.apply(this, arguments);
              };
            }()).then(function (data) {
              // If we have no data, set loaded
              if (!data) {
                setLoaded(true);
                return;
              }

              checkForData(data, fetchOrder);
            }).catch(onFail));

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  })), [checkForData]);
  useEffect(function () {
    if (!loaded) {
      fetchOrder();
    } else {
      showReturnMessage();
    }
  }, [loaded, fetchOrder]);

  if (cancelled) {
    return /*#__PURE__*/_jsx(Tracking, {
      bopis: bopis,
      cancelled: cancelled,
      cancelledAt: cancelled && cancelledAt,
      loading: false,
      trackers: trackers,
      transactions: transactions
    });
  }

  return /*#__PURE__*/_jsx(Tracking, {
    bopis: bopis,
    fulfillmentStatus: fulfillmentStatus,
    loading: !loaded,
    trackers: trackers,
    transactions: transactions
  });
};

Loader.propTypes = {
  bopis: PropTypes.bool,
  cancelled: PropTypes.bool,
  cancelledAt: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  fulfillmentStatus: PropTypes.string,
  shopifyTracker: PropTypes.shape({})
};

var init = function init() {
  if (!document.querySelector('#order')) {
    return;
  } // Initialize the account nav with the correct configuration


  ReactDOM.render( /*#__PURE__*/_jsx(OVIDProvider, {
    domain: COOKIE_DOMAIN,
    name: JWT_COOKIE_NAME,
    url: OV_ID_URL,
    children: /*#__PURE__*/_jsx(AccountNav, {
      ovIDURL: OV_ID_URL,
      shopifyURL: "/"
    })
  }), document.getElementById('account-nav'));
  var shopifyTracker = normalizeShopifyData(ORDER);
  var bopis = ORDER.bopis,
      cancelled = ORDER.cancelled,
      cancelledAt = ORDER.cancelledAt,
      fulfillmentStatus = ORDER.fulfillmentStatus;
  ReactDOM.render( /*#__PURE__*/_jsx(OVApp, {
    children: /*#__PURE__*/_jsx(Loader, {
      bopis: bopis,
      cancelled: cancelled,
      cancelledAt: cancelledAt,
      fulfillmentStatus: fulfillmentStatus,
      shopifyTracker: shopifyTracker
    })
  }), document.getElementById('fulfillments'));
};

export default init;